import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Link,
  Card,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PreviewIcon from "@mui/icons-material/Preview";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SendIcon from "@mui/icons-material/Send";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import html2pdf from "html2pdf.js";

import api from "../../utils/api";
import ArchitecturalLetterTemplate from "./ArchitecturalLetterTemplate";
import useCompanyLogo from "../../utils/useCompanyLogo";

const ArchitecturalRequestLetterView = ({
  member,
  requestDetails,
  currentUserId,
}) => {
  const logoUrl = useCompanyLogo();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("preview");
  const [editableContent, setEditableContent] = useState("");

  const [recipientEmail, setRecipientEmail] = useState(member?.EmailAddress || "");
  const [ccEmail, setCcEmail] = useState("");

  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagInputs, setTagInputs] = useState({});
  const [manualTags, setManualTags] = useState({});

  const [pdfPreview, setPdfPreview] = useState({ open: false, src: "" });

  const letterRef = useRef();

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  const formatHtmlAddress = (htmlAddress) => {
    if (!htmlAddress) return "";
    const plain = htmlAddress
      .replace(/<br\s*\/?>/gi, ", ")
      .replace(/\s+/g, " ")
      .trim();

    const parts = plain.split(",").map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) {
      return htmlAddress;
    }

    const street = parts[0];
    const cityStateZip = parts.slice(1).join(", ");

    const formattedCityStateZip = cityStateZip.replace(
      /,\s*([A-Za-z]{2})\s*,?\s*(\d{5}(?:-\d{4})?)?/,
      (match, state, zip) => `, ${state.toUpperCase()}${zip ? " " + zip : ""}`
    );

    return `${street}<br/>${formattedCityStateZip}`;
  };

  const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, "").trim();

  const parseCityStateZip = (addrPlain) => {
    const parts = (addrPlain || "")
      .replace(/\s+/g, " ")
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);

    let city = "";
    let state = "";
    let zip = "";

    if (parts.length >= 3) {
      city = parts[1] || "";
      const stateZip = parts[2] || "";
      const stateMatch = stateZip.match(/\b([A-Za-z]{2})\b/);
      const zipMatch = stateZip.match(/(\d{5}(?:-\d{4})?)/);
      state = stateMatch ? stateMatch[1].toUpperCase() : "";
      zip = zipMatch ? zipMatch[1] : "";
      return { city, state, zip };
    }

    if (parts.length === 2) {
      const cityStateZip = parts[1];
      const zipMatch = cityStateZip.match(/(\d{5}(?:-\d{4})?)/);
      zip = zipMatch ? zipMatch[1] : "";
      const stateMatch = cityStateZip.match(/\b([A-Za-z]{2})\b/);
      state = stateMatch ? stateMatch[1].toUpperCase() : "";

      city = cityStateZip
        .replace(zip, "")
        .replace(stateMatch?.[1] || "", "")
        .replace(/\s+/g, " ")
        .trim();

      return { city, state, zip };
    }

    return { city: "", state: "", zip: "" };
  };

  const today = new Date().toLocaleDateString("en-US");

  const formattedAddress = useMemo(() => {
    const fromHtml = formatHtmlAddress(member?.FullAddressHtml || "");
    if (fromHtml) return fromHtml;
    return member?.FullAddress ? member.FullAddress.replace(/\n/g, "<br/>") : "";
  }, [member?.FullAddressHtml, member?.FullAddress]);

  const addressPlain = useMemo(() => {
    return stripHtml(member?.FullAddressHtml || member?.FullAddress || "");
  }, [member?.FullAddressHtml, member?.FullAddress]);

  const { city, state, zip } = useMemo(
    () => parseCityStateZip(addressPlain),
    [addressPlain]
  );

  const requestTypeName = useMemo(() => {
    return (
      requestDetails?.request_type_name ||
      requestDetails?.type_name ||
      requestDetails?.request_type?.[1] ||
      requestDetails?.request_type?.type ||
      requestDetails?.request_type ||
      ""
    );
  }, [requestDetails]);

  const requestSubtypeName = useMemo(() => {
    return (
      requestDetails?.subtype_name ||
      requestDetails?.request_subtype_name ||
      requestDetails?.subtype?.subtype_name ||
      ""
    );
  }, [requestDetails]);

  const propertyAddressPlain = (member?.FullAddressHtml || member?.FullAddress || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

  const daysToComplete = useMemo(() => {
    const v = requestDetails?.completion_days_current?.days_to_complete;
    return v === 0 || v ? v : null;
  }, [requestDetails?.completion_days_current?.days_to_complete]);

  const baseTags = useMemo(
    () => ({
      date: today,
      name: `${member?.FirstName || ""} ${member?.LastName || ""}`.trim(),
      email: recipientEmail || member?.EmailAddress || "N/A",
      address: formattedAddress || "N/A",
      property_address: propertyAddressPlain || "N/A",
      city: city || "",
      state: state || "",
      zip: zip || "",
      id: requestDetails?.id || "",
      request_id: requestDetails?.id || "",
      property_id: requestDetails?.property_id || "",
      description: requestDetails?.description || "",
      status: requestDetails?.status || "",
      priority: requestDetails?.priority || "",
      requested_at: requestDetails?.requested_at
        ? new Date(requestDetails.requested_at).toLocaleDateString("en-US")
        : "",
      request_type: requestTypeName || "N/A",
      request_subtype: requestSubtypeName || "N/A",
      conditions: requestDetails?.conditions || "",
      days_to_complete: daysToComplete ?? "N/A",
      completion_days: daysToComplete ?? "N/A",
    }),
    [
      today,
      member?.FirstName,
      member?.LastName,
      member?.EmailAddress,
      recipientEmail,
      formattedAddress,
      propertyAddressPlain,
      city,
      state,
      zip,
      requestDetails?.id,
      requestDetails?.property_id,
      requestDetails?.description,
      requestDetails?.status,
      requestDetails?.priority,
      requestDetails?.requested_at,
      requestDetails?.conditions,
      daysToComplete,
      requestTypeName,
      requestSubtypeName,
    ]
  );

  const tags = useMemo(() => ({ ...baseTags, ...manualTags }), [baseTags, manualTags]);

  const applyTags = (html, mapOverride) => {
    if (!html) return "";
    let output = html;
    const map = mapOverride || tags;

    Object.entries(map).forEach(([key, value]) => {
      const safeValue = typeof value === "string" ? value.replace(/\n/g, "<br/>") : value;
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "gi");
      output = output.replace(regex, safeValue ?? "");
    });

    return output;
  };

  const extractTags = (html) => {
    if (!html) return [];
    const regex = /{{\s*(.*?)\s*}}/g;
    const matches = [...html.matchAll(regex)];
    return [...new Set(matches.map((m) => m[1]))];
  };

  const allTagsUsed = extractTags(editableContent);
  const providedKeys = new Set(Object.keys(tags));
  const missingTags = allTagsUsed.filter((t) => !providedKeys.has(t));

  const handleApplyManualTags = () => {
    if (!missingTags.length) {
      setTagModalOpen(false);
      return;
    }

    const updatedManualTags = { ...manualTags, ...tagInputs };
    setManualTags(updatedManualTags);
    setEditableContent((prev) =>
      applyTags(prev, { ...baseTags, ...updatedManualTags })
    );
    setTagModalOpen(false);
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await api.get("/architectural-letter-templates");
        const data = res.data || [];
        setTemplates(data);

        if (data.length > 0) {
          const firstTpl = data[0];
          setSelectedTemplateId(firstTpl.id);
          setEditableContent(applyTags(firstTpl.content, { ...baseTags, ...manualTags }));
        }
      } catch (err) {
        console.error("Failed to load architectural templates:", err);
      } finally {
        setLoading(false);
      }
    };

    if (requestDetails?.id) fetchTemplates();
  }, [requestDetails?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tpl = templates.find((t) => t.id === selectedTemplateId);
    if (!tpl) return;
    setEditableContent(applyTags(tpl.content, { ...baseTags, ...manualTags }));
  }, [
    selectedTemplateId,
    templates.length,
    JSON.stringify(baseTags),
    JSON.stringify(manualTags),
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTemplateChange = (id) => {
    setSelectedTemplateId(id);
    const tpl = templates.find((t) => t.id === id);
    if (tpl) setEditableContent(applyTags(tpl.content, { ...baseTags, ...manualTags }));
  };

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  const buildSubjectAndFilename = () => {
    const tplName = selectedTemplate?.name || "Architectural Letter";
    const reqId = requestDetails?.id || "Letter";
    const subject = `${tplName} #${reqId}`;
    const filename = `${tplName.replace(/\s+/g, "_")}_REQ_${reqId}.pdf`;
    return { subject, filename };
  };

  const buildServerPDFUrl = (fileUrlOrPath) => `${api.defaults.baseURL}/${fileUrlOrPath}`;

  const generateLetter = async () => {
    if (!letterRef.current) return null;

    const { subject, filename } = buildSubjectAndFilename();

    await Promise.all(
      Array.from(letterRef.current.querySelectorAll("img"))
        .filter((img) => !img.complete)
        .map(
          (img) =>
            new Promise((resolve) => {
              img.onload = img.onerror = resolve;
            })
        )
    );

    if (document.fonts && document.fonts.ready) await document.fonts.ready;

    letterRef.current.style.maxWidth = "816px";
    letterRef.current.style.margin = "0 auto";
    letterRef.current.style.padding = "0.25in";
    letterRef.current.style.backgroundColor = "#ffffff";

    const pdfBlob = await html2pdf()
      .set({
        margin: [0.5, 0.5, 0.5, 0.5],
        filename,
        image: { type: "jpeg", quality: 1.0 },
        html2canvas: {
          scale: 2,
          scrollY: 0,
          useCORS: true,
          backgroundColor: "#ffffff",
          letterRendering: true,
        },
        jsPDF: {
          unit: "in",
          format: "letter",
          orientation: "portrait",
          compress: false,
        },
        pagebreak: { mode: ["css", "legacy"] },
      })
      .from(letterRef.current)
      .outputPdf("blob");

    const uploadData = new FormData();
    uploadData.append("pdf", pdfBlob, filename);
    uploadData.append("architectural_request_id", requestDetails?.id || "");
    uploadData.append("template_id", selectedTemplate?.id || "");
    uploadData.append("recipient", recipientEmail);
    uploadData.append("content", editableContent);

    const uploadRes = await api.post(
      "/architectural-requests/upload-letter-pdf",
      uploadData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    const pdf_path = uploadRes.data?.pdf_path;
    const file_id = uploadRes.data?.file_id;
    const file_url = uploadRes.data?.file_url;

    const formData = new FormData();
    formData.append("pdf", pdfBlob, filename);
    formData.append("recipient", recipientEmail);
    formData.append("cc", ccEmail || "");
    formData.append("subject", subject);
    formData.append("content", editableContent);
    formData.append("architectural_request_id", requestDetails?.id || "");
    formData.append("template_id", selectedTemplate?.id || "");
    formData.append("template_type", selectedTemplate?.type || "");
    formData.append("pdf_path", pdf_path || "");

    return { pdfBlob, filename, subject, formData, pdf_path, file_id, file_url };
  };

  const handleExportLetter = async () => {
    try {
      const { pdf_path, file_id } = await generateLetter();

      if (!pdf_path) {
        alert("PDF was not generated on the server.");
        return;
      }

      const res = await api.get(
        `/architectural-requests/${requestDetails.id}/letter-files`
      );
      if (!res.data?.length) {
        alert("PDF saved, but server returned no files.");
        return;
      }

      const latest = res.data[0];
      const fileUrl = latest.file_url;

      const serverPdfUrl = buildServerPDFUrl(
        `architecturalletters/${requestDetails.id}/${encodeURIComponent(fileUrl)}`
      );

      setPdfPreview({ open: true, src: serverPdfUrl });

      await api.put(`/architectural-requests/${requestDetails.id}/log-letter`, {
        action: "export",
        template_id: selectedTemplate?.id,
        address_type: "email",
        recipient: recipientEmail,
        template_body: editableContent,
        pdf_path,
        sent_by: currentUserId,
        file_id,
      });
    } catch (err) {
      console.error("Error exporting letter:", err);
      alert("Failed to export letter");
    }
  };

  const handleEmailLetter = async () => {
    if (!recipientEmail) {
      alert("Please enter a recipient email before sending.");
      return;
    }

    try {
      const { formData, pdf_path, file_id } = await generateLetter();

      if (!formData) return;

      await api.post("/send-architectural-letter", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await api.put(`/architectural-requests/${requestDetails.id}/log-letter`, {
        action: "email",
        template_id: selectedTemplate?.id,
        address_type: "email",
        recipient: recipientEmail,
        template_body: editableContent,
        pdf_path,
        sent_by: currentUserId,
        file_id,
      });

      alert("Letter emailed and logged.");
    } catch (err) {
      console.error("Error sending letter:", err);
      alert("Failed to send letter");
    }
  };

  if (!member || !requestDetails) {
    return (
      <Typography variant="body2" color="error">
        Loading letter details...
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "220px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (templates.length === 0) {
    return (
      <Typography variant="body2" color="warning.main">
        No architectural templates found.
      </Typography>
    );
  }

  return (
    <Box>
      <Card sx={{ ...pageCardSx, overflow: "hidden", mb: 3 }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            Architectural Letter
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Preview, edit, export, or email a generated letter for this request.
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            sx={{ mb: 2.5, justifyContent: "space-between", alignItems: { md: "center" } }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                icon={<InfoOutlinedIcon />}
                label={`Template: ${selectedTemplate?.name || "N/A"}`}
                variant="outlined"
                size="small"
              />
              <Chip
                label={`Request #${requestDetails?.id || "N/A"}`}
                variant="outlined"
                size="small"
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleExportLetter}
                sx={secondaryButtonSx}
              >
                Export Letter as PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleEmailLetter}
                sx={primaryButtonSx}
              >
                Email Letter
              </Button>
              <Button
                variant="outlined"
                color={missingTags.length ? "warning" : "primary"}
                onClick={() => setTagModalOpen(true)}
                sx={secondaryButtonSx}
              >
                Enter Missing Info{missingTags.length ? ` (${missingTags.length})` : ""}
              </Button>
            </Stack>
          </Stack>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Template</InputLabel>
            <Select
              value={selectedTemplateId}
              label="Select Template"
              onChange={(e) => handleTemplateChange(e.target.value)}
              sx={textFieldSx}
            >
              {templates.map((tpl) => (
                <MenuItem key={tpl.id} value={tpl.id}>
                  {tpl.name} ({tpl.type})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            required
            label="Recipient Email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            sx={{ ...textFieldSx, mb: 2 }}
            placeholder="Enter recipient email"
          />

          <TextField
            fullWidth
            label="CC (optional)"
            value={ccEmail}
            onChange={(e) => setCcEmail(e.target.value)}
            sx={{ ...textFieldSx, mb: 2 }}
            placeholder="cc1@email.com, cc2@email.com"
          />

          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, val) => val && setViewMode(val)}
              sx={{
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid rgba(47,99,173,0.08)",
                "& .MuiToggleButton-root": {
                  px: 1.75,
                  border: "none",
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#5f6b7a",
                },
                "& .Mui-selected": {
                  color: "#2f63ad !important",
                  backgroundColor: "rgba(47,99,173,0.08)",
                },
              }}
            >
              <ToggleButton value="preview">
                <PreviewIcon sx={{ mr: 1 }} />
                Preview
              </ToggleButton>
              <ToggleButton value="edit">
                <EditNoteIcon sx={{ mr: 1 }} />
                Edit HTML
              </ToggleButton>
              <ToggleButton value="rich">Rich Text</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {viewMode === "preview" ? (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "14px",
                backgroundColor: "#fff",
                border: "1px solid rgba(47,99,173,0.08)",
              }}
            >
              <div ref={letterRef}>
                <ArchitecturalLetterTemplate
                  templateName={selectedTemplate?.name}
                  member={member}
                  requestDetails={{
                    ...requestDetails,
                    type_name: requestTypeName,
                    subtype_name: requestSubtypeName,
                  }}
                  formattedAddress={formattedAddress}
                  date={new Date()}
                  content={editableContent}
                  logoUrl={logoUrl}
                />
              </div>
            </Paper>
          ) : viewMode === "edit" ? (
            <TextField
              fullWidth
              multiline
              minRows={20}
              variant="outlined"
              value={editableContent}
              onChange={(e) => setEditableContent(e.target.value)}
              sx={{ ...textFieldSx, mt: 1 }}
            />
          ) : (
            <Box sx={{ mt: 2 }}>
              <ReactQuill
                theme="snow"
                value={editableContent}
                onChange={(val) => setEditableContent(val)}
                style={{ height: "400px", marginBottom: "40px" }}
              />
            </Box>
          )}
        </Box>
      </Card>

      <Dialog
        open={tagModalOpen}
        onClose={() => setTagModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Enter Required Information</DialogTitle>
        <DialogContent>
          {missingTags.length === 0 ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              No additional tags detected in this template.
            </Typography>
          ) : (
            missingTags.map((t) => (
              <TextField
                key={t}
                margin="dense"
                fullWidth
                label={t.replace(/_/g, " ")}
                value={tagInputs[t] || ""}
                onChange={(e) =>
                  setTagInputs((prev) => ({
                    ...prev,
                    [t]: e.target.value,
                  }))
                }
                sx={textFieldSx}
              />
            ))
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.25 }}>
          <Button onClick={() => setTagModalOpen(false)} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleApplyManualTags}
            variant="contained"
            disabled={missingTags.length === 0}
            sx={primaryButtonSx}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pdfPreview.open}
        onClose={() => setPdfPreview({ open: false, src: "" })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "black",
            p: 2,
          }}
        >
          <IconButton
            onClick={() => setPdfPreview({ open: false, src: "" })}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              bgcolor: "rgba(0,0,0,0.4)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon sx={{ color: "white" }} />
          </IconButton>

          <Box sx={{ width: "100%", height: "90vh", maxWidth: "1200px" }}>
            <object
              data={pdfPreview.src}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            >
              <Typography color="white">
                PDF preview not supported.{" "}
                <Link href={pdfPreview.src} target="_blank" rel="noopener" sx={{ color: "#90caf9" }}>
                  Open PDF
                </Link>
              </Typography>
            </object>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              href={pdfPreview.src}
              target="_blank"
              download
              sx={primaryButtonSx}
            >
              Download PDF
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ArchitecturalRequestLetterView;
